import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import {
  LOGIN_ENDPOINT,
  LOGOUT_ENDPOINT,
  POST_FORM_ENDPOINT,
  POST_RAW_ENDPOINT,
  PROCUREMENT_LOGIN_ENDPOINT,
} from "../utils/constants";

const HANDLERS = {
  INITIALIZE: "INITIALIZE",
  SIGN_IN: "SIGN_IN",
  SIGN_OUT: "SIGN_OUT",
};

const initialState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
};

const handlers = {
  [HANDLERS.INITIALIZE]: (state, action) => {
    const user = action.payload;

    return {
      ...state,
      ...(user
        ? {
            isAuthenticated: true,
            isLoading: false,
            user,
          }
        : {
            isLoading: false,
          }),
    };
  },
  [HANDLERS.SIGN_IN]: (state, action) => {
    const user = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  [HANDLERS.SIGN_OUT]: (state) => {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({ undefined });

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const initialized = useRef(false);

  const [deviceInfo, setDeviceInfo] = useState({
    device_os_api_level: "",
    device_screen_height: "",
    device_screen_width: "",
    device_name: "",
    device_model: "",
    device_manufacturer: "",
  });

  useEffect(() => {
    const getDeviceInformation = () => {
      const userAgent = navigator.userAgent;

      // Extracting device name, model, and manufacturer from the userAgent
      const deviceName = userAgent.match(/\(([^)]+)\)/);
      const [deviceModel, deviceManufacturer] = deviceName
        ? deviceName[1].split(";")
        : ["", ""];

      const getOSName = () => {
        const userAgent = navigator.userAgent;
        if (userAgent.includes("Win")) return "Windows";
        if (userAgent.includes("Linux")) return "Linux";
        if (userAgent.includes("Mac")) return "Mac OS";
        // Add more conditions for other operating systems if needed
        return "Unknown OS";
      };

      setDeviceInfo({
        device_os_api_level: getOSName(),
        device_screen_height: window.screen.height,
        device_screen_width: window.screen.width,
        device_name: deviceName ? deviceName[1] : "",
        device_model: deviceModel.trim(),
        device_manufacturer: deviceManufacturer.trim(),
      });
    };

    getDeviceInformation();
  }, []);

  const initialize = async () => {
    if (initialized.current) {
      return;
    }

    initialized.current = true;

    let isAuthenticated = false;

    try {
      isAuthenticated = localStorage.getItem("authenticated") === "true";
    } catch (err) {
      console.error(err);
    }

    if (isAuthenticated) {
      const user = {
        id: "5e86809283e28b96d2d38537",
        avatar: "/assets/avatars/avatar-anika-visser.png",
        name: "Anika Visser",
        email: "anika.visser@devias.io",
      };

      dispatch({
        type: HANDLERS.INITIALIZE,
        payload: user,
      });
    } else {
      dispatch({
        type: HANDLERS.INITIALIZE,
      });
    }
  };

  const skip = () => {
    try {
      localStorage.setItem("authenticated", "true");
    } catch (err) {
      console.error(err);
    }

    const user = {
      id: "5e86809283e28b96d2d38537",
      avatar: "/assets/avatars/avatar-anika-visser.png",
      name: "Anika Visser",
      email: "anika.visser@devias.io",
    };

    dispatch({
      type: HANDLERS.SIGN_IN,
      payload: user,
    });
  };

  const signIn = async (phoneNumber, password) => {
    try {
      const response = await axios.post(
        PROCUREMENT_LOGIN_ENDPOINT,
        {
          device_name: deviceInfo.device_name,
          device_model: deviceInfo.device_model,
          device_product: "256998",
          device_os_api_level: deviceInfo.device_os_api_level,
          device_manufacturer: deviceInfo.device_manufacturer,
          device_screen_height: deviceInfo.device_screen_height,
          device_screen_width: deviceInfo.device_screen_width,
          device_sd_card_state: "False",
        },
        {
          headers: {
            Userid: password,
            Token: "N/A",
            url: LOGIN_ENDPOINT,
            Securitycode:
              "ipj0DjOqyd7faka1aa9z1dA1mXESh1zfo5QV8PuuPnJwadTfal3ujesPspAgA2Nb",
            Phonenumber: phoneNumber,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data.success) {
        toast.error(response.data.message);
        await delay(2000);
        const errorMessage = response.data.message || "Authentication failed";
        throw new Error(errorMessage);
      } else {
        toast.success(response.data.message);
        await delay(500);
        // Save user data to localStorage
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userData", JSON.stringify(response.data));
        localStorage.setItem("authenticated", "true");

        dispatch({
          type: HANDLERS.SIGN_IN,
          payload: response.data,
        });
      }
    } catch (err) {
      toast.error(err);
      throw new Error(err.message || "Authentication failed");
    }
  };
  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  const signOut = async () => {
    try {
      const storedToken = localStorage.getItem("token").toString();
      const response = await axios.post(
        POST_FORM_ENDPOINT,
        {},
        {
          headers: {
            Token: storedToken,
            url: LOGOUT_ENDPOINT,
          },
        }
      );

      if (!response.data.success) {
        toast.error(response.data.message);
        await delay(2000);
        const errorMessage = response.data.message || "Authentication failed";
        throw new Error(errorMessage);
      } else {
        toast.success(response.data.message);
        await delay(2000);
        localStorage.removeItem("token");
        localStorage.removeItem("authenticated");
        localStorage.removeItem("userData");

        dispatch({
          type: HANDLERS.SIGN_OUT,
        });
      }
    } catch (error) {
      toast.error(error);
      console.error("Error during sign out:", error);
    }
  };
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    try {
      const savedIsDarkMode = localStorage.getItem("isDarkMode");
      if (savedIsDarkMode !== null) {
        setIsDarkMode(JSON.parse(savedIsDarkMode));
      }
    } catch (error) {
      console.error("Error parsing isDarkMode from local storage:", error);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        skip,
        signIn,
        signOut,
        isDarkMode,
        setIsDarkMode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export const AuthConsumer = AuthContext.Consumer;

export const useAuthContext = () => useContext(AuthContext);
